import React, { useEffect, useState } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { Avatar, Dialog, DialogContent, DialogTitle } from '@mui/material';
import { CloudUpload } from 'lucide-react';
import * as z from 'zod';

import Button from '../common/Button';
import { toastPromise } from '../common/utils';
import { useActivateUser, useGetUser } from '../hooks/tanstack/useUserHook';
import { useGetRouteParams } from '../hooks/tanstack/useUtilsHook';
import cn from '../utils/cn';

import FormInput from './form/FormInput';

const AccountSetupSchema = z
  .object({
    first_name: z.string().min(1, "First name can't be empty").trim(),
    last_name: z.string().min(1, "Last name can't be empty").trim(),
    password: z
      .string()
      .trim()
      .min(8, 'Password must be at least 8 characters'),
    confirm_password: z
      .string()
      .trim()
      .min(8, 'Password must be at least 8 characters'),
    profile_pic: z.custom<FileList>(),
  })
  .refine((data) => data.password === data.confirm_password, {
    message: 'Confirm password does not match',
    path: ['confirmPassword'],
  });

type AccountSetupSchemaType = z.infer<typeof AccountSetupSchema>;

type CreateModelFormDialogProps = {
  open: boolean;
  title: string;
  handleCloseDialog: () => void;
};

const AccountSetupDialog = ({
  open,
  title,
  handleCloseDialog,
}: CreateModelFormDialogProps) => {
  const [imageSrc, setImageSrc] = useState<string | ArrayBuffer | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const [error, setError] = useState();

  const { projectId, orgId } = useGetRouteParams();

  const { data: currentUser } = useGetUser();

  const { mutateAsync: activateUser, isPending: isActivatingUser } =
    useActivateUser({
      onSuccess: () => {
        reset();
        handleCloseDialog();
      },
    });

  const form = useForm<AccountSetupSchemaType>({
    resolver: zodResolver(AccountSetupSchema),
  });

  const {
    handleSubmit,
    watch,
    setError: setFormError,
    setValue,
    reset,
    formState,
  } = form;

  const { isValid } = formState;

  useEffect(() => {
    if (!error) {
      return;
    }

    Object.keys(error as unknown as Object).forEach((key) =>
      setFormError(key as keyof AccountSetupSchemaType, {
        message: error?.[key] as unknown as string,
      })
    );
  }, [error]);

  const onSubmit: SubmitHandler<AccountSetupSchemaType> = async (data) => {
    if (!currentUser) {
      return;
    }

    const res = activateUser({
      userId: currentUser.id,
      data: {
        ...data,
        profile_pic: data.profile_pic?.[0] as File,
      },
    });

    toastPromise({
      promise: res,
      content: 'Your changes has been updated successfully',
    });
  };

  const isDisabled = !isValid || isActivatingUser;

  const onDrop = () => {
    setIsDragOver(false);
  };

  const onDragOver = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleUploadImage = (e: { target: { files: any } }) => {
    const formData = new FormData();

    Array.from(e.target.files as File[]).forEach((file: File) => {
      formData.append('avatar', file);
    });

    setValue('profile_pic', e.target.files);

    const reader = new FileReader();

    reader.onloadend = () => {
      setImageSrc(reader.result);
    };

    reader.readAsDataURL(e.target.files?.[0]);
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle className="w-full">
        <h2 className="pt-2 text-2xl font-bold">{title}</h2>
      </DialogTitle>
      <DialogContent>
        <div>
          <FormProvider {...form}>
            <form onSubmit={handleSubmit(onSubmit)} className="mb-2 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 gap-2 flex">
                  <FormInput
                    label="First Name"
                    name="first_name"
                    placeholder="Enter your first name"
                    inputProps={{
                      maxLength: 50,
                    }}
                  />
                  <FormInput
                    label="Last Name"
                    name="last_name"
                    placeholder="Enter your last name"
                    inputProps={{
                      maxLength: 50,
                    }}
                  />
                </div>
                <div className="col-span-2 flex flex-col gap-2">
                  <label className="font-semibold pb-2 text-sm text-[#666]">
                    Profile picture
                  </label>
                  <div className="flex gap-4">
                    <Avatar
                      alt="avatar"
                      src={imageSrc as string}
                      sx={{
                        height: 70,
                        width: 70,
                        rounded: 'full',
                      }}
                    />
                    <div
                      onDrop={onDrop}
                      onDragOver={onDragOver}
                      onDragLeave={onDrop}
                      className={cn(
                        'relative transition cursor-pointer flex w-full flex-col items-center gap-3 rounded-[4px] border border-[#CCC] py-6 text-[#666]',
                        {
                          'bg-[#F5F9FF] border-dashed border-[#2196F3] text-[#2196F3]':
                            isDragOver,
                        }
                      )}
                    >
                      <input
                        type="file"
                        accept=".png,.jpg"
                        onChange={handleUploadImage}
                        className="absolute h-full w-full opacity-0"
                      />
                      <CloudUpload width={30} height={30} />
                      <div className="space-y-1 text-center">
                        <p>
                          <span className="font-semibold text-primary-red">
                            Click to upload
                          </span>{' '}
                          or drag and drop
                        </p>
                        <p>PNG or JPG (max. 800x800px)</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-span-2 gap-2 flex">
                  <FormInput label="Password" name="password" type="password" />
                </div>
                <div className="col-span-2 gap-2 flex">
                  <FormInput
                    label="Confirm password"
                    name="confirm_password"
                    type="password"
                  />
                </div>
              </div>
              <div className="flex-end flex justify-end gap-3">
                <Button
                  type="submit"
                  loading={isActivatingUser}
                  variant="contained_blue"
                  disabled={isActivatingUser}
                >
                  Save
                </Button>
              </div>
            </form>
          </FormProvider>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AccountSetupDialog;
